import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/endpoints';
import { ApiError } from '../api/client';
import type { StationOrder } from '../api/types';
import { usePoll } from '../hooks/usePoll';
import { elapsedSince, formatMoney } from '../lib/format';
import { Button } from './ui';
import { useToast } from './Toast';

// The counter being called. A customer has pressed "Send order" on their
// station, and until someone here accepts or declines it, nothing happens:
// the drinks do not get made, and the seat cannot order again.
//
// So this is built to be impossible to miss and impossible to lose. It lives
// in the app shell, not on a page, so PC-3's order rings whichever screen is
// open. It stays up until it is dealt with — there is no dismiss, because
// dismissing is not one of the two things that can be done with an order. It
// chimes when an order arrives and keeps chiming while any is waiting, and
// the tab title carries the count so a dashboard that is behind another
// window still says so. Mute exists because a loud thing with no off switch
// gets the whole tab closed, and is remembered per browser.
//
// Everything on the card comes from the order, with the prices the customer
// confirmed at. Accepting puts exactly those on the tab; the backend does the
// folding into existing lines and returns the whole updated tab, which is
// why the toast can say what the customer now owes.

const POLL_MS = 4000;
/** Not a nag every second, not so rare a busy counter forgets. */
const REPEAT_CHIME_MS = 15_000;
const MUTE_KEY = 'hhg.order_alerts.muted';

export function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

// A two-note chime from the Web Audio API — no asset to serve, and nothing
// the browser has to download before it can ring. The context is created on
// first use: browsers keep one suspended until the page has had a click, and
// every staff member has clicked at least once to log in.
let audio: AudioContext | null = null;
export function chime(): void {
  try {
    audio ??= new AudioContext();
    if (audio.state === 'suspended') void audio.resume();
    const start = audio.currentTime;
    for (const [offset, frequency] of [
      [0, 880],
      [0.18, 1174.66],
    ] as const) {
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.type = 'sine';
      osc.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, start + offset);
      gain.gain.exponentialRampToValueAtTime(0.35, start + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + 0.35);
      osc.connect(gain).connect(audio.destination);
      osc.start(start + offset);
      osc.stop(start + offset + 0.4);
    }
  } catch {
    // No audio on this device. The banner and the title still carry it.
  }
}

export function OrderAlerts() {
  const toast = useToast();
  const navigate = useNavigate();
  const { data, refresh } = usePoll(
    (signal) => api.fnb.orders('pending', signal),
    POLL_MS,
  );
  const orders = data ?? [];

  const [muted, setMuted] = useState(readMuted);
  const [busy, setBusy] = useState<string | null>(null);

  // Ring once per order the first time it is seen, then keep ringing on a
  // slow repeat while anything is waiting. The set of seen ids is a ref, not
  // state: it is bookkeeping, and changing it must not redraw the bar.
  const seen = useRef<Set<string>>(new Set());
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  useEffect(() => {
    let fresh = false;
    for (const order of orders) {
      if (!seen.current.has(order.order_id)) {
        seen.current.add(order.order_id);
        fresh = true;
        // A system notification as well, for the dashboard that is behind the
        // game a member of staff is watching. Only if it was allowed already;
        // asking is done on a click below, where browsers permit it.
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          try {
            new Notification(`Order from ${order.station_name}`, {
              body: `${order.customer_name}: ${describe(order)} · ${formatMoney(order.total)}`,
              tag: order.order_id,
            });
          } catch {
            // Notifications refused by the platform. Nothing to do.
          }
        }
      }
    }
    if (fresh && !mutedRef.current) chime();
  }, [orders]);

  useEffect(() => {
    if (orders.length === 0) return;
    const timer = window.setInterval(() => {
      if (!mutedRef.current) chime();
    }, REPEAT_CHIME_MS);
    return () => window.clearInterval(timer);
  }, [orders.length]);

  // "(2 orders) HHG Manager" in the tab strip.
  useEffect(() => {
    const base = 'HHG Manager';
    document.title =
      orders.length === 0
        ? base
        : `(${orders.length} ${orders.length === 1 ? 'order' : 'orders'}) ${base}`;
    return () => {
      document.title = base;
    };
  }, [orders.length]);

  // Browsers only let a page ask for notification permission from a click.
  // The first click anywhere after the bar mounts is the moment to ask.
  useEffect(() => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'default') return;
    const ask = (): void => void Notification.requestPermission();
    window.addEventListener('pointerdown', ask, { once: true });
    return () => window.removeEventListener('pointerdown', ask);
  }, []);

  const toggleMute = useCallback(() => {
    setMuted((current) => {
      const next = !current;
      try {
        localStorage.setItem(MUTE_KEY, next ? '1' : '0');
      } catch {
        // Private mode. Forgotten on reload, which is fine.
      }
      return next;
    });
  }, []);

  const resolve = async (
    order: StationOrder,
    action: 'accept' | 'decline',
  ): Promise<void> => {
    setBusy(order.order_id);
    try {
      if (action === 'accept') {
        const result = await api.fnb.acceptOrder(order.order_id);
        toast.success(
          `${describe(order)} added to ${order.customer_name}'s tab (${order.station_name}). ` +
            `Owes ${formatMoney(result.tab.due_amount)}.`,
        );
      } else {
        await api.fnb.declineOrder(order.order_id);
        toast.info(`${order.station_name}'s order declined. Let ${order.customer_name} know.`);
      }
    } catch (err) {
      // 409 is the other member of staff getting there first. That is not an
      // error at this end; it is news.
      if (err instanceof ApiError && err.status === 409) toast.info(err.message);
      else toast.error((err as Error).message);
    } finally {
      setBusy(null);
      refresh();
    }
  };

  if (orders.length === 0) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="border-t border-rose-500/40 bg-rose-950/70 px-4 py-3 backdrop-blur"
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-sm font-semibold text-rose-100">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-400" />
          </span>
          {orders.length === 1
            ? 'A station is ordering'
            : `${orders.length} stations are ordering`}
        </p>
        <button
          onClick={toggleMute}
          className="text-xs text-rose-200/80 underline-offset-2 hover:text-rose-100 hover:underline"
        >
          {muted ? 'Sound off — turn on' : 'Mute sound'}
        </button>
      </div>

      <ul className="flex flex-col gap-2">
        {orders.map((order) => (
          <li
            key={order.order_id}
            className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-rose-400/40 bg-slate-950/60 px-4 py-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-lg font-bold text-white">{order.station_name}</span>
                <button
                  onClick={() => navigate(`/customers/${order.customer_id}`)}
                  className="text-sm text-sky-300 hover:underline"
                >
                  {order.customer_name}
                </button>
                <span className="text-xs text-slate-400">{age(order.placed_at)}</span>
              </div>
              <p className="mt-0.5 text-sm text-slate-100">
                {byVendor(order).length === 0 && describe(order)}
                <span className="ml-2 font-mono font-semibold text-rose-100">
                  {formatMoney(order.total)}
                </span>
              </p>
              {/* Which kitchen to call for what, when an order spans vendors. */}
              {byVendor(order).map(([vendor, lines]) => (
                <p key={vendor} className="text-sm text-slate-100">
                  <span className="font-semibold text-rose-200">{vendor}:</span> {lines}
                </p>
              ))}
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => void resolve(order, 'decline')}
                disabled={busy === order.order_id}
              >
                Decline
              </Button>
              <Button
                onClick={() => void resolve(order, 'accept')}
                disabled={busy === order.order_id}
              >
                {busy === order.order_id ? 'Working…' : 'Add to tab'}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** "just now", then "3m ago". */
function age(iso: string): string {
  const elapsed = elapsedSince(iso);
  return elapsed === '0m' ? 'just now' : `${elapsed} ago`;
}

/** The lines grouped by the kitchen that makes them. Empty when nothing on
 *  the order has a vendor (the counter's own stock). */
function byVendor(order: StationOrder): [string, string][] {
  if (!order.items.some((item) => item.vendor_name)) return [];
  const groups = new Map<string, string[]>();
  for (const item of order.items) {
    const vendor = item.vendor_name ?? 'Counter';
    const label = item.qty === 1 ? item.item_name : `${item.item_name} ×${item.qty}`;
    groups.set(vendor, [...(groups.get(vendor) ?? []), label]);
  }
  return [...groups].map(([vendor, lines]) => [vendor, lines.join(', ')]);
}

/** "Coke 300ml ×2, Lays" — the lines, compactly. */
function describe(order: StationOrder): string {
  return order.items
    .map((item) => (item.qty === 1 ? item.item_name : `${item.item_name} ×${item.qty}`))
    .join(', ');
}
